<script>
// * Used in weekly view
export default {
    name: 'EventItemPreview',

    props: {
        occasion: {
            type: Object,
            required: true
        },
        monthlyView: {
            type: Boolean,
            default: false
        },
        weeklyView: {
            type: Boolean,
            default: false
        },
        revealOnHover: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            mouseOver: false
        }
    },

    mounted() {
        if ( !this.revealOnHover ) { return }

        // * mouseOver debug
        // this.mouseOver = true

        const cardRoot = this.$refs.cardRoot
        cardRoot.addEventListener('mouseenter', () => {
            if ( this.mouseOver ) { return }
            this.mouseOver = true
        })

        cardRoot.addEventListener('mouseleave', () => {
            if ( !this.mouseOver ) { return }
            this.mouseOver = false
        })
    },
}
</script>

<template>
    <div
        class="h-100"
        style="padding: 4px;"
    >
        <article
            ref="cardRoot"
            class="event-card-calendar preview h-100 d-flex flex-column"
            :class="{ 'monthly-view' : monthlyView }"
        >
            <div class="param">
                <div
                    :class="occasion.style"
                    class="color-coded-section"
                >
                    <div class="colored-line" />
                </div>
                <div
                    :class="occasion.style"
                    class="color-coded-section"
                >
                    <div
                        class="hour mt-2"
                        :class="{ 'mb-2' : monthlyView || !occasion.location }"
                    >
                        <div class="icon-wrap">
                            <svg class="icon icon-hour"><use xlink:href="#icon-hour" /></svg>
                        </div>
                        <span>{{ occasion.time }}</span>
                    </div>
                </div>
                <div
                    v-if="!monthlyView"
                    :class="occasion.style"
                    class="color-coded-section"
                >
                    <div
                        v-if="occasion.location"
                        class="location my-2"
                    >
                        <div class="icon-wrap">
                            <svg class="icon icon-pin"><use xlink:href="#icon-pin-cal" /></svg>
                        </div>
                        <span>{{ occasion.location.name }}</span>
                    </div>
                </div>
                <div
                    class="type color-coded-section"
                    :class="occasion.style"
                >
                    <div
                        v-for="(category, idx) in occasion.categories"
                        :key="idx"
                    >
                        <a :href="category.url">
                            {{ category.name }}
                        </a>
                    </div>
                </div>
                <div
                    v-if="occasion.series"
                    class="mt-2 occasion-series"
                >
                    <div
                        class="series-name"
                    >
                        <a
                            :href="occasion.series.url"
                            target="_blank"
                        >
                            {{ occasion.series.name }}
                        </a>
                    </div>
                </div>
                <!--  weekly view genre -->
                <!-- <div v-if="!monthlyView">
                    <div
                        v-if="mouseOver && occasion.genres && occasion.genres.length"
                        class="text-left genre mb-3 mt-2"
                    >
                        <div>
                            {{ __('Genre') }}:
                            <span
                                v-for="(genre, idx) in occasion.genres"
                                :key="idx"
                            >
                                <a
                                    :href="genre.url"
                                >
                                    {{ genre.name }}<span v-if="idx < occasion.genres.length-1">,&nbsp;</span>
                                </a>
                            </span>
                        </div>
                    </div>
                </div> -->
            </div>
            <div
                class="title limit-text-3 labels"
                :class="{ 'monthly-view' : monthlyView }"
            >
                <span
                    v-if="occasion.isOnline"
                    class="label label-secondary"
                >
                    {{ __('Online') }}
                </span>
                <span
                    v-if="occasion.isPostponed"
                    class="label label-error"
                >
                    {{ __('Postponed') }}
                </span>
                <h3>
                    <a
                        :href="occasion.url"
                        target="_blank"
                    >
                        {{ occasion.name }}
                    </a>
                </h3>
            </div>
            <div
                v-if="monthlyView && occasion.isSoldOut"
                class="title labels"
                :class="{ 'monthly-view' : monthlyView }"
            >
                <span class="label label-secondary">
                    {{ __('All tickets are sold out') }}
                </span>
            </div>
            <transition-expand>
                <div
                    v-if="monthlyView"
                    class="param m-0"
                >
                    <div
                        v-if="mouseOver"
                        :class="occasion.style"
                        class="color-coded-section"
                    >
                        <div
                            v-if="occasion.location"
                            class="location my-2"
                        >
                            <div class="icon-wrap">
                                <svg class="icon icon-pin"><use xlink:href="#icon-pin-cal" /></svg>
                            </div>
                            <span>{{ occasion.location.name }}</span>
                        </div>
                    </div>
                    <div
                        v-if="mouseOver && occasion.genres && occasion.genres.length"
                        class="text-left genre mb-3 mt-2"
                    >
                        <div>
                            {{ __('Genre') }}:
                            <span
                                v-for="(genre, idx) in occasion.genres"
                                :key="idx"
                            >
                                <a
                                    :href="genre.url"
                                >
                                    {{ genre.name }}<span v-if="idx < occasion.genres.length-1">,&nbsp;</span>
                                </a>
                            </span>
                        </div>
                    </div>
                </div>
            </transition-expand>
            <transition-expand>
                <div
                    v-if="monthlyView && mouseOver"
                    class="controls text-right"
                    :class="{ 'monthly-view' : monthlyView }"
                >
                    <a
                        :href="occasion.button.url"
                        target="_blank"
                        class="h-auto"
                        :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                        :tabindex="occasion.button.disabled ? -1 : 0"
                    >
                        {{ occasion.button.label }}
                    </a>

                    <a
                        v-if="occasion.stbutton.url"
                        :href="occasion.stbutton.url"
                        target="_blank"
                        class="h-auto"
                        :class="[occasion.stbutton.style]"
                    >
                        {{ occasion.stbutton.label }}
                    </a>

                    <a
                        :href="occasion.url"
                        target="_blank"
                        class="btn-more"
                    >
                        {{ __('Details') }} <svg class="icon icon-shortarrow"><use xlink:href="#icon-shortarrow" /></svg>
                    </a>
                </div>
            </transition-expand>
            <!-- <div class="date-wrap">
                <div class="date">
                    <svg class="icon icon-calendar"><use xlink:href="#icon-calendar" /></svg>
                    <span class="timestamp">{{ occasion.fancyDate }}</span>
                </div>
            </div> -->
            <div
                v-if="!monthlyView"
                class="mt-auto controls text-right"
            >
                <a
                    :href="occasion.button.url"
                    target="_blank"
                    class="h-auto"
                    :class="[occasion.button.style, { 'disabled': occasion.button.disabled }]"
                    :tabindex="occasion.button.disabled ? -1 : 0"
                >
                    {{ occasion.button.label }}
                </a>

                <a
                    v-if="occasion.stbutton.url"
                    :href="occasion.stbutton.url"
                    target="_blank"
                    class="h-auto"
                    :class="[occasion.stbutton.style]"
                >
                    {{ occasion.stbutton.label }}
                </a>

                <a
                    :href="occasion.url"
                    target="_blank"
                    class="btn-more"
                >
                    {{ __('Details') }} <svg class="icon icon-shortarrow"><use xlink:href="#icon-shortarrow" /></svg>
                </a>
            </div>
            <transition-expand>
                <a
                    v-if="(monthlyView || weeklyView)&& mouseOver"
                    :href="occasion.url"
                    class="cover"
                >
                    <img
                        :src="occasion.image"
                        :alt="occasion.name"
                    >
                </a>
            </transition-expand>
        </article>
    </div>
</template>

<style scoped lang="scss">
.occasion-series {
    .series-name {
        padding-left: 6px;
        border-left: 6px solid #0068B1;
    }
}
</style>
